import "~/assets/main.css";
import GlobalProgress from "components/global-progress";
import { ModalContainer } from "components/modal/components/container";
import StickyBannerContainer from "components/sticky-banner";
import ToastContainer from "components/toast";
import { WindowManager } from "components/window-manager";
import { useUserStore } from "@/stores/user";
import { hasSession } from "@/auth/auth0";
import { getFindexSessionId, useSession } from "@/modules/auth/hooks/useSession";
import { checkHealthOrShowError } from "@/modules/app/hooks/useHealthCheck.ts";
import { until } from "@/utils/async/until";
import { useFullscreenLoader } from "components/fullscreen-loader/hooks/useFullscreenLoader";
import { checkEmailVerified } from "@/modules/user/hooks/useUserVerified";
import FullscreenLoader from "components/fullscreen-loader";
import FeatureBaseComponent from "@/integrations/featurebase";
import { globalModals } from "components/modal/hooks/useModals";
import { useAsyncFetch } from "@/utils/async/useAsyncFetch";
import { useFindexSocket } from "./composables/useFindexSocket";
import { BinaryKeyValueStore } from "socker/shared";
import { ESocketClientEvent } from "socker/client";
import { AlertContainer } from "components/alerts/components/container";
import { DrawerContainer } from "components/drawer/components/container";

const init = async () => {
  if (hasSession()) {
    const userStore = useUserStore();
    console.log("userStore.init()");
    await userStore.init();
  }
};

const view = defineComponent(() => {
  const session = useSession();
  const fullLoader = useFullscreenLoader();
  const initResult = useAsyncFetch(async () => {
    await checkHealthOrShowError();
    await until(
      () => session.state.microTaskExecuted === true || session.isAlive.value,
      0,
      90000,
    );
    await init();
    checkEmailVerified(session.state.user);
    return true;
  });

  const socket = useFindexSocket();

  let timer: ReturnType<typeof setTimeout> | null = null;
  const authenticate = async (token: string) => {
    console.log(`Socket is ready!`);
    console.log("Authenticating with server...");
    socket.send(
      BinaryKeyValueStore.fromJS({
        app: "auth",
        action: "AUTH",
        token: token,
        sessionId: getFindexSessionId()
      }),
    );
    const resp = await socket.receive({ app: "auth", action: "AUTH" }, 10000);
    if (!resp) {
      console.warn(`Failed to authenticate with socket server (timeout).`);
      return;
    }
    const respJs = resp.toJS() as { ok?: boolean; error?: string };
    if (respJs.ok === false) {
      console.warn(`Failed to authenticate with socket server:`, respJs.error);
      return;
    }
    console.log(`Successfully authenticated with socket server.`);

    setTimeout(() => {
      socket.send(BinaryKeyValueStore.fromJS({
        app: 'onlineUsers',
        action: 'USER_LOGIN'
      }));
    }, 1000);
  };
  const unwatch = watch(
    session.isAlive,
    (isAlive) => {
      if (timer !== null) return;
      if (isAlive) {
        const token = session.state.token;
        if (token) {
          timer = setTimeout(() => {
            socket.onReady(async () => {
              unwatch();
              await authenticate(token);
            });
            socket.events.subscribe(ESocketClientEvent.RECONNECTED, () => {
              authenticate(token).catch(e => console.error(e));
            });
          }, 500);
        }
      }
    },
    { immediate: true },
  );

  const isLoading = computed(
    () =>
      initResult.loading.value === true || fullLoader.loading.value === true,
  );

  return () => {
    return (
      <div
        style={{
          width: "100%",
          height: "100svh",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <StickyBannerContainer />
        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <NuxtLayout>
            <NuxtPage />
          </NuxtLayout>
        </div>

        <DrawerContainer/>
        <ModalContainer hook={globalModals} />
        <AlertContainer/>
        <ToastContainer />
        <GlobalProgress />
        <WindowManager />
        <FeatureBaseComponent />

        {isLoading.value && <FullscreenLoader />}
      </div>
    );
  };
});

export default view;
